import { sortAsc } from '../utils/sortOptions';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, ClockIcon, PlusIcon, XMarkIcon,
  DocumentTextIcon, PrinterIcon, ArrowUpTrayIcon, EyeIcon, TrashIcon,
  PencilSquareIcon, FunnelIcon,
} from '@heroicons/react/24/outline';
import { fmtDateDMY } from '../requestDisplay';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useSchoolModules } from '../hooks/useSchoolModules';

// ── Shared styles ─────────────────────────────────────────────────────────────
const sharedCss = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fp * { box-sizing:border-box; }
  .fp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:fpIn .35s ease both; }
  @keyframes fpIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
  .fp-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .fp-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .fp-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-teal  { background:#0d9488; color:#fff; }
  .btn-teal:hover  { background:#0b7b71; box-shadow:0 4px 14px rgba(13,148,136,.35); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .sc-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .sc-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-hdr { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .pill { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .pill-pending  { background:#fffbeb; color:#f59e0b; }
  .pill-approved { background:#ecfdf5; color:#10c98e; }
  .pill-rejected { background:#fef2f2; color:#ef4444; }
  .pill-draft    { background:#f0f2f7; color:#6b7194; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .filter-row { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .fsel,.finp { font-family:'Sora',sans-serif; font-size:.78rem; padding:7px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; }
  .fsel:focus,.finp:focus { border-color:#0d9488; background:white; }
  .tabs { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.2rem; width:fit-content; flex-wrap:wrap; }
  .tab { padding:6px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; background:transparent; color:#9094a8; }
  .tab.on { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  /* Modals are portaled to document.body, outside .fp, so the .fp * box-sizing
     reset below never reaches them — Tailwind preflight isn't active on this
     page (no tailwind.config.js), so without this every input/select/textarea
     defaults to content-box and renders wider than its 100% width by its own
     padding+border, overflowing its grid column. */
  .mb, .mb * { box-sizing:border-box; }
  /* Modal — matches Admin's Expenses/Assets modal architecture exactly:
     backdrop itself scrolls (align-items:flex-start + overflow-y:auto) and the
     modal box is a plain block (not a flex column with its own max-height cap),
     with only .mo-bd capped at 62vh. */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:flex-start; justify-content:center; z-index:9999; padding:24px 16px; overflow-y:auto; }
  .mo { background:white; border-radius:20px; width:100%; max-width:600px; margin:auto; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; gap:8px; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 26px 20px 22px; max-height:62vh; overflow-y:auto; overflow-x:hidden; scrollbar-gutter:stable; box-sizing:border-box; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .fg { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .ff.full { grid-column:1/-1; }
  .fl { font-size:.7rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs,.ft { box-sizing:border-box; font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus,.ft:focus { border-color:#0d9488; background:white; }
  .ft { resize:vertical; min-height:70px; }
  .req-notice { background:#f0fdfa; border:1px solid #99f6e4; border-radius:10px; padding:10px 14px; font-size:.76rem; color:#0f766e; margin-bottom:14px; }

  .fp-cards { display:none; padding:10px; }
  .fp-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .fp-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .fp-card-title { font-size:.84rem; font-weight:700; color:#0f1123; }
  .fp-card-sub { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .fp-card-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .fp-card-row:last-of-type { border-bottom:none; }
  .fp-card-label { color:#9094a8; font-weight:600; flex-shrink:0; }
  .fp-card-val { text-align:right; }
  .fp-card-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }

  @media (max-width:768px) {
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .fp-title { font-size:1.1rem; }
    .fg { grid-template-columns:1fr; }
    .ff.full { grid-column:1; }
    .tbl-wrap { overflow-x:auto; display:none !important; }
    .fp-cards { display:block; }
    .mo { max-width:100%; border-radius:16px 16px 0 0; max-height:95vh; }
    .mb { padding:0 10px; align-items:flex-end; }
  }
`;

const fmt = n => (n === null || n === undefined || n === '') ? '—' : `Rs. ${Number(n).toLocaleString('en-PK')}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }).replace(/ /g,'-') : '—';
const curMonth = () => new Date().toISOString().slice(0,7);
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }) : '—';

// ── Check-in card (shared shape across Admin/Finance/Coordinator dashboards) ──
const checkinCardCss = `
  .checkin-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
  .checkin-info  { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .checkin-dot   { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .checkin-dot.in  { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .checkin-dot.out { background:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,.2); }
  .checkin-dot.off { background:#d1d5db; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px rgba(16,201,142,.2)} 50%{box-shadow:0 0 0 6px rgba(16,201,142,.05)} }
  .checkin-label { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .checkin-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .checkin-times { display:flex; gap:1.5rem; flex-shrink:0; flex-wrap:wrap; }
  .checkin-time-item  { text-align:center; }
  .checkin-time-label { font-size:.65rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.05em; }
  .checkin-time-val   { font-size:.9rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#1a1d2e; margin-top:2px; }
  .checkin-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .checkin-btn svg { width:15px; height:15px; stroke-width:2; }
  .checkin-btn.green { background:#10c98e; color:white; }
  .checkin-btn.green:hover { background:#0db87e; box-shadow:0 4px 14px rgba(16,201,142,.35); transform:translateY(-1px); }
  .checkin-btn.red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .checkin-btn.red:hover   { background:#ef4444; color:white; }
  .checkin-btn.ghost { background:#f0f2f7; color:#9094a8; }
  .checkin-btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  @media (max-width:768px) { .checkin-card { flex-direction:column; align-items:flex-start; gap:.8rem; } }
`;
const CheckinCard = ({ today, actioning, onCheckIn, onCheckOut }) => {
  if (!today?.checkinEnabled) return null;
  const rec    = today?.record;
  const hasIn  = !!rec?.checkIn;
  const hasOut = !!rec?.checkOut;
  return (
    <div className="checkin-card"><style>{checkinCardCss}</style>
      <div className="checkin-info">
        <div className={`checkin-dot ${hasOut?'out':hasIn?'in':'off'}`}/>
        <div>
          <div className="checkin-label">{hasOut?'Checked Out':hasIn?'Currently Checked In':'Not Checked In'}</div>
          <div className="checkin-sub">{rec?.lateMinutes>0?`${rec.lateMinutes}m late today`:'Right on time'}</div>
        </div>
      </div>
      {(hasIn||hasOut)&&(
        <div className="checkin-times">
          <div className="checkin-time-item"><div className="checkin-time-label">Check In</div><div className="checkin-time-val">{fmtTime(rec?.checkIn)}</div></div>
          {hasOut&&<div className="checkin-time-item"><div className="checkin-time-label">Check Out</div><div className="checkin-time-val">{fmtTime(rec?.checkOut)}</div></div>}
          {hasOut&&rec?.workHours&&<div className="checkin-time-item"><div className="checkin-time-label">Hours</div><div className="checkin-time-val">{Number(rec.workHours||0).toFixed(1)}h</div></div>}
        </div>
      )}
      <div style={{width:'100%',maxWidth:200}}>
        {!hasIn&&<button className="checkin-btn green" onClick={onCheckIn} disabled={actioning}><CheckCircleIcon/>{actioning?'Please wait…':'Check In'}</button>}
        {hasIn&&!hasOut&&<button className="checkin-btn red" onClick={onCheckOut} disabled={actioning}><XCircleIcon/>{actioning?'Please wait…':'Check Out'}</button>}
        {hasOut&&<button className="checkin-btn ghost" disabled>Done for today</button>}
      </div>
    </div>
  );
};

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
export const FinanceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    api.get('/finance/dashboard').then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
    fetchToday();
  }, []);

  const fetchToday = async () => {
    try { const { data: res } = await api.get('/finance/attendance/today'); setToday(res); } catch { /* silent */ }
  };
  const handleCheckIn = async () => {
    try { setActioning(true); await api.post('/finance/attendance/checkin'); toast.success('Checked in!'); fetchToday(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-in failed'); }
    finally { setActioning(false); }
  };
  const handleCheckOut = async () => {
    try { setActioning(true); await api.post('/finance/attendance/checkout'); toast.success('Checked out!'); fetchToday(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-out failed'); }
    finally { setActioning(false); }
  };

  const TYPE_LABEL = {
    fee_action:'Fee Action', expense_add:'Add Expense', expense_edit:'Edit Expense',
    expense_delete:'Delete Expense', asset_add:'Add Asset', asset_edit:'Edit Asset',
    asset_delete:'Delete Asset', asset_maintenance:'Asset Maintenance',
    salary_action:'Salary Action', letter_character:'Character Certificate',
    letter_school_leaving:'School Leaving Certificate', letter_experience:'Experience Letter',
  };

  if (loading) return <div className="fp"><style>{sharedCss}</style><div className="loading"><div className="spin"/>Loading…</div></div>;

  return (
    <div className="fp"><style>{sharedCss}</style>
      <div className="fp-hdr">
        <div><div className="fp-title">Finance Dashboard</div><div className="fp-sub">Overview of finance activities and requests</div></div>
      </div>

      <CheckinCard today={today} actioning={actioning} onCheckIn={handleCheckIn} onCheckOut={handleCheckOut} />

      <div className="stat-grid">
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#fffbeb'}}><ClockIcon style={{color:'#f59e0b'}}/></div>
          <div className="sc-label">Pending Requests</div>
          <div className="sc-value">{data?.stats?.pendingRequests || 0}</div>
          <div className="sc-sub">awaiting approval</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#ecfdf5'}}><CheckCircleIcon style={{color:'#10c98e'}}/></div>
          <div className="sc-label">Approved</div>
          <div className="sc-value">{data?.stats?.approvedRequests || 0}</div>
          <div className="sc-sub">this period</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#fef2f2'}}><XCircleIcon style={{color:'#ef4444'}}/></div>
          <div className="sc-label">Rejected</div>
          <div className="sc-value">{data?.stats?.rejectedRequests || 0}</div>
          <div className="sc-sub">this period</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#f0fdfa'}}><DocumentTextIcon style={{color:'#0d9488'}}/></div>
          <div className="sc-label">Total Letters</div>
          <div className="sc-value">{data?.stats?.totalLetters || 0}</div>
          <div className="sc-sub">issued</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-hdr"><div className="panel-title">Recent Requests</div></div>
        {!data?.recentRequests?.length ? (
          <div className="empty">No requests yet.</div>
        ) : (
          <>
          <div className="tbl-wrap" style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Type</th><th>Status</th><th>Your Note</th><th>Admin Note</th><th>Date</th></tr></thead>
              <tbody>
                {data.recentRequests.map(r => (
                  <tr key={r._id}>
                    <td style={{fontWeight:600}}>{TYPE_LABEL[r.type] || r.type}</td>
                    <td><span className={`pill pill-${r.status}`}>{r.status}</span></td>
                    <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:180}}>{r.note || '—'}</td>
                    <td style={{fontSize:'.76rem',color:r.status==='rejected'?'#ef4444':'#10c98e',maxWidth:180}}>{r.adminNote || '—'}</td>
                    <td><span className="mono">{fmtDateDMY(r.createdAt)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fp-cards">
            {data.recentRequests.map(r => (
              <div key={r._id} className="fp-card">
                <div className="fp-card-top">
                  <div className="fp-card-title">{TYPE_LABEL[r.type] || r.type}</div>
                  <span className={`pill pill-${r.status}`}>{r.status}</span>
                </div>
                <div className="fp-card-row"><span className="fp-card-label">Your Note</span><span className="fp-card-val">{r.note || '—'}</span></div>
                <div className="fp-card-row"><span className="fp-card-label">Admin Note</span><span className="fp-card-val" style={{color:r.status==='rejected'?'#ef4444':'#10c98e'}}>{r.adminNote || '—'}</span></div>
                <div className="fp-card-row"><span className="fp-card-label">Date</span><span className="mono">{fmtDateDMY(r.createdAt)}</span></div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── EXPENSES (same as admin — only difference is request-approval) ───────────
const EXP_SUB_OPTIONS = {
  electricity: ['Monthly Bill','Wiring Repair','Meter Replacement','New Connection','Transformer','Other'],
  generator:   ['Fuel Refill','Engine Oil Change','Filter Change','Belt Replacement','Coolant','Full Service','Repair','Spare Parts','Other'],
  water:       ['Monthly Bill','Tank Cleaning','Pipe Repair','Pump Repair','New Pipe Installation','Other'],
  rent:        ['Monthly Rent','Security Deposit','Advance','Other'],
  salaries:    ['Teaching Staff','Non-Teaching Staff','Security','Cleaning Staff','Driver','Other'],
  stationery:  ['Paper/Registers','Pens/Pencils','Printer Ink','Whiteboard Markers','Other'],
  maintenance: ['Plumbing','Electrical Work','Painting','Carpentry','Cleaning','Ceiling/Roof','Floor','Windows/Doors','AC Service','Other'],
  repair:      ['Furniture','Electronics','Vehicle','Building','Other'],
  transport:   ['Fuel','Vehicle Repair','Insurance','Registration/Tax','Driver Salary','Other'],
  equipment:   ['Purchase','Repair','Calibration','Other'],
  furniture:   ['Purchase','Repair','Polish/Refinish','Other'],
  events:      ['Sports Day','Annual Function','Exam Fee','Prize Distribution','Trip','Other'],
  miscellaneous:['General Expense','Refreshments','Printing','Medical','Other'],
};
const EXP_CATEGORIES = Object.keys(EXP_SUB_OPTIONS);
const EXP_CAT_COLORS = {
  electricity:'#f59e0b', generator:'#ef4444', water:'#3b6ef8', rent:'#8b5cf6',
  salaries:'#10c98e', stationery:'#6366f1', maintenance:'#f97316', repair:'#ec4899',
  transport:'#14b8a6', equipment:'#0ea5e9', furniture:'#84cc16', events:'#a855f7', miscellaneous:'#9094a8',
};
const EXP_CAT_BG = {
  electricity:'#fffbeb', generator:'#fef2f2', water:'#eef2ff', rent:'#f3f0ff',
  salaries:'#ecfdf5', stationery:'#eef2ff', maintenance:'#fff7ed', repair:'#fdf2f8',
  transport:'#f0fdfa', equipment:'#f0f9ff', furniture:'#f7fee7', events:'#faf5ff', miscellaneous:'#f0f2f7',
};
const EXP_CAT_ICONS = {
  electricity:'⚡', generator:'🔧', water:'💧', rent:'🏠', salaries:'👥',
  stationery:'📝', maintenance:'🛠️', repair:'🔩', transport:'🚌',
  equipment:'⚙️', furniture:'🪑', events:'🎉', miscellaneous:'📦',
};
const EXP_EMPTY_FORM = { title:'', category:'electricity', subcategory:'', amount:'', date:new Date().toISOString().slice(0,10), description:'', vendor:'', receiptNo:'', paymentMethod:'cash', meterReading:'', previousReading:'', note:'', module:'school' };

const expChartCss = `
  .charts-row { display:grid; grid-template-columns:2fr 1fr; gap:1rem; margin-bottom:1.4rem; }
  .chart-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; }
  .chart-title { font-size:.86rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .chart-sub   { font-size:.72rem; color:#9094a8; margin-bottom:1rem; }
  @media (max-width:900px) { .charts-row{grid-template-columns:1fr;} }
`;

const ExpChartTooltip = ({ active, payload, label }) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:'white',border:'1px solid #e8ebf4',borderRadius:8,padding:'8px 12px',fontSize:'.76rem',boxShadow:'0 4px 14px rgba(0,0,0,.08)'}}>
      <div style={{fontWeight:700,marginBottom:4,color:'#0f1123'}}>{label}</div>
      {payload.map((p,i)=><div key={i} style={{color:'#6b7194'}}>{p.name}: <strong style={{color:'#ef4444'}}>{fmt(p.value)}</strong></div>)}
    </div>
  );
};

export const FinanceExpenses = () => {
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const showModuleTabs = libraryEnabled || hostelEnabled;
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [form, setForm] = useState(EXP_EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterMonth, setFilterMonth] = useState(curMonth());
  const [filterCategory, setFilterCategory] = useState('');
  const [filterModule, setFilterModule] = useState('');

  useEffect(() => { fetchAll(); }, [filterMonth, filterCategory, filterModule]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterMonth)    params.month    = filterMonth;
      if (filterCategory) params.category = filterCategory;
      if (filterModule)   params.module   = filterModule;
      const [expRes, sumRes] = await Promise.all([
        api.get('/finance/expenses', { params }),
        api.get('/finance/expenses/summary'),
      ]);
      setExpenses(expRes.data || []);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  const sf = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const openAdd = () => { setEditTarget(null); setForm(EXP_EMPTY_FORM); setModal(true); };
  const openEdit = (exp) => {
    setEditTarget(exp);
    setForm({
      title:exp.title, category:exp.category, subcategory:exp.subcategory||'',
      amount:exp.amount, date:exp.date?.slice(0,10)||'',
      description:exp.description||'', vendor:exp.vendor||'',
      receiptNo:exp.receiptNo||'', paymentMethod:exp.paymentMethod||'cash',
      meterReading:exp.meterReading||'', previousReading:exp.previousReading||'', note:'',
      module:exp.module||'school',
    });
    setModal(true);
  };

  const handleCatChange = (cat) => setForm(f => ({ ...f, category:cat, subcategory:'', title: EXP_SUB_OPTIONS[cat]?.[0]||'' }));

  const submitRequest = async () => {
    if (!form.amount || !form.date) return toast.error('Amount and date required');
    try {
      setSaving(true);
      const { note, ...rest } = form;
      const payload = { ...rest, title: rest.title || rest.subcategory || rest.category };
      const type = editTarget ? 'expense_edit' : 'expense_add';
      if (editTarget) payload._id = editTarget._id;
      await api.post('/finance/requests', { type, payload, note });
      toast.success('Request submitted for admin approval');
      setModal(false);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const submitDelete = async () => {
    try {
      setSaving(true);
      await api.post('/finance/requests', { type: 'expense_delete', payload: { _id: delTarget._id, title: delTarget.title }, note: `Delete expense: ${delTarget.title}` });
      toast.success('Delete request submitted');
      setDelTarget(null);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const totalFiltered = expenses.reduce((s,e) => s + (e.amount||0), 0);
  const pieData = summary?.categoryBreakdown?.map(c => ({ name:c._id, value:c.total, fill:EXP_CAT_COLORS[c._id]||'#9094a8' })) || [];
  const subs = EXP_SUB_OPTIONS[form.category] || [];

  return (
    <div className="fp"><style>{sharedCss}</style>
      <div className="fp-hdr">
        <div><div className="fp-title">Expenses</div><div className="fp-sub">Track school spending · request add/edit/delete (admin approval required)</div></div>
        <button className="btn btn-teal" onClick={openAdd}><PlusIcon />Request Add</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="sc-icon" style={{background:'#fef2f2'}}>💸</div><div className="sc-label">This Month</div><div className="sc-value" style={{fontSize:'.95rem'}}>{fmt(summary?.thisMonth?.total)}</div><div className="sc-sub">{summary?.thisMonth?.count||0} expenses</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#fffbeb'}}>📊</div><div className="sc-label">This Year</div><div className="sc-value" style={{fontSize:'.95rem'}}>{fmt(summary?.thisYear?.total)}</div><div className="sc-sub">total spending</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#eef2ff'}}>🔍</div><div className="sc-label">Filtered Total</div><div className="sc-value" style={{fontSize:'.95rem'}}>{fmt(totalFiltered)}</div><div className="sc-sub">{expenses.length} records</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#ecfdf5'}}>🏆</div><div className="sc-label">Top Category</div><div className="sc-value" style={{fontSize:summary?.categoryBreakdown?.[0]?._id?'.85rem':'.76rem',textTransform:'capitalize',color:summary?.categoryBreakdown?.[0]?._id?'#0f1123':'#b0b5cc'}}>{summary?.categoryBreakdown?.[0]?._id||'No data yet'}</div><div className="sc-sub">{summary?.categoryBreakdown?.[0]?._id?fmt(summary.categoryBreakdown[0].total):''}</div></div>
      </div>

      {summary && (
        <div className="charts-row"><style>{expChartCss}</style>
          <div className="chart-panel">
            <div className="chart-title">Monthly Expenses</div>
            <div className="chart-sub">Last 6 months spending</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary.monthlyChart} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false}/>
                <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={48} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}K`:v}/>
                <Tooltip content={<ExpChartTooltip/>}/>
                <Bar dataKey="total" name="Total" fill="#ef4444" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-panel">
            <div className="chart-title">By Category</div>
            <div className="chart-sub">This year</div>
            {pieData.length>0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={2}>
                    {pieData.map((e,i)=><Cell key={i} fill={e.fill}/>)}
                  </Pie>
                  <Tooltip formatter={v=>fmt(v)}/>
                  <Legend wrapperStyle={{fontFamily:'Sora',fontSize:10}}/>
                </PieChart>
              </ResponsiveContainer>
            ) : <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'#9094a8',fontSize:'.8rem'}}>No data yet</div>}
          </div>
        </div>
      )}

      {showModuleTabs && (
        <div className="cat-tabs"><style>{`.cat-tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:1.2rem;} .cat-tab{padding:5px 12px;border-radius:99px;border:1.5px solid #e0e4f0;background:white;font-family:'Sora',sans-serif;font-size:.75rem;font-weight:600;cursor:pointer;color:#6b7194;} .cat-tab.active{background:#0d9488;color:white;border-color:#0d9488;}`}</style>
          <button className={`cat-tab${!filterModule?' active':''}`} onClick={()=>setFilterModule('')}>All</button>
          {hostelEnabled  && <button className={`cat-tab${filterModule==='hostel'?' active':''}`}  onClick={()=>setFilterModule('hostel')}>🏠 Hostel</button>}
          {libraryEnabled && <button className={`cat-tab${filterModule==='library'?' active':''}`} onClick={()=>setFilterModule('library')}>📚 Library</button>}
          <button className={`cat-tab${filterModule==='school'?' active':''}`} onClick={()=>setFilterModule('school')}>🏫 School</button>
        </div>
      )}

      <div className="filter-row">
        <FunnelIcon style={{width:15,height:15,stroke:'#9094a8',flexShrink:0}}/>
        <input type="month" className="finp" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} />
        <select className="fsel" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="">All Categories</option>
          {sortAsc(EXP_CATEGORIES).map(c => <option key={c} value={c}>{EXP_CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={() => { setFilterMonth(''); setFilterCategory(''); }}>Reset</button>
      </div>

      <div className="panel">
        <div className="panel-hdr">
          <div className="panel-title">Expense Records <span style={{fontWeight:400,color:'#9094a8',marginLeft:8,fontSize:'.76rem'}}>{expenses.length} records · {fmt(totalFiltered)}</span></div>
        </div>
        {loading ? <div className="loading"><div className="spin"/>Loading…</div>
        : !expenses.length ? <div className="empty">No expenses found. <button className="btn btn-teal btn-sm" style={{marginLeft:8}} onClick={openAdd}><PlusIcon />Add First</button></div>
        : <>
          <div className="tbl-wrap" style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Date</th><th>Category</th><th>Sub-type</th><th>Title</th><th>Description</th><th>Vendor</th><th>Method</th><th>Amount</th><th>Actions</th></tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e._id}>
                    <td><span className="mono">{fmtDate(e.date)}</span></td>
                    <td><span className="pill" style={{background:EXP_CAT_BG[e.category]||'#f0f2f7',color:EXP_CAT_COLORS[e.category]||'#6b7194'}}>{EXP_CAT_ICONS[e.category]} {e.category}</span></td>
                    <td>{e.subcategory && <span className="pill pill-draft">{e.subcategory}</span>}</td>
                    <td style={{fontWeight:600}}>{e.title}</td>
                    <td style={{fontSize:'.76rem',color:'#9094a8',maxWidth:160}}>
                      <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={e.description||''}>{e.description || '—'}</div>
                    </td>
                    <td style={{fontSize:'.76rem',color:'#9094a8'}}>{e.vendor || '—'}</td>
                    <td style={{fontSize:'.74rem',color:'#9094a8',textTransform:'capitalize'}}>{e.paymentMethod}</td>
                    <td><span className="mono" style={{color:'#ef4444',fontWeight:700}}>{fmt(e.amount)}</span></td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(e)}><PencilSquareIcon /></button>
                        <button className="btn btn-red btn-sm" onClick={() => setDelTarget(e)}><TrashIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fp-cards">
            {expenses.map(e => (
              <div key={e._id} className="fp-card">
                <div className="fp-card-top">
                  <div>
                    <div className="fp-card-title">{e.title}</div>
                    <div className="fp-card-sub">{e.vendor || '—'}</div>
                  </div>
                  <span className="mono" style={{color:'#ef4444',fontWeight:700}}>{fmt(e.amount)}</span>
                </div>
                <div className="fp-card-row"><span className="fp-card-label">Category</span><span className="pill" style={{background:EXP_CAT_BG[e.category]||'#f0f2f7',color:EXP_CAT_COLORS[e.category]||'#6b7194'}}>{EXP_CAT_ICONS[e.category]} {e.category}</span></div>
                {e.subcategory&&<div className="fp-card-row"><span className="fp-card-label">Sub-type</span><span className="pill pill-draft">{e.subcategory}</span></div>}
                <div className="fp-card-row"><span className="fp-card-label">Date</span><span className="mono">{fmtDate(e.date)}</span></div>
                {e.description&&<div className="fp-card-row"><span className="fp-card-label">Description</span><span style={{textAlign:'right'}}>{e.description}</span></div>}
                <div className="fp-card-row"><span className="fp-card-label">Payment</span><span style={{textTransform:'capitalize'}}>{e.paymentMethod}</span></div>
                <div className="fp-card-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(e)}><PencilSquareIcon />Edit</button>
                  <button className="btn btn-red btn-sm" onClick={() => setDelTarget(e)}><TrashIcon />Delete</button>
                </div>
              </div>
            ))}
          </div>
          </>}
      </div>

      {modal && createPortal(
        <div className="mb" onClick={() => setModal(false)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">{editTarget ? 'Request Edit Expense' : 'Request Add Expense'}</div><button className="mo-x" onClick={() => setModal(false)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <div className="req-notice">This will be sent to admin for approval before taking effect.</div>
              <div className="fg">
                {showModuleTabs && (
                  <div className="ff"><label className="fl">Belongs To</label><select className="fs" value={form.module} onChange={sf('module')}>
                    <option value="school">🏫 School</option>
                    {hostelEnabled  && <option value="hostel">🏠 Hostel</option>}
                    {libraryEnabled && <option value="library">📚 Library</option>}
                  </select></div>
                )}
                <div className="ff"><label className="fl">Category</label><select className="fs" value={form.category} onChange={e => handleCatChange(e.target.value)}>{EXP_CATEGORIES.map(c => <option key={c} value={c}>{EXP_CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}</select></div>
                <div className="ff"><label className="fl">Sub-type</label><select className="fs" value={form.subcategory} onChange={sf('subcategory')}><option value="">— Select type —</option>{subs.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="ff full"><label className="fl">Title</label><input type="text" className="fi" value={form.title} onChange={sf('title')} placeholder={form.subcategory ? `e.g. ${form.subcategory} - ${new Date().toLocaleDateString('en-US',{month:'long'})}` : 'e.g. Electricity Bill - March'} /></div>
                <div className="ff full"><label className="fl">Description</label><textarea className="ft" value={form.description} onChange={sf('description')} placeholder="Optional additional details" rows={2} /></div>
                <div className="ff"><label className="fl">Amount (Rs.) *</label><input type="number" className="fi" value={form.amount} onChange={sf('amount')} /></div>
                <div className="ff"><label className="fl">Date *</label><input type="date" className="fi" value={form.date} onChange={sf('date')} /></div>
                <div className="ff"><label className="fl">Payment Method</label><select className="fs" value={form.paymentMethod} onChange={sf('paymentMethod')}><option value="cash">Cash</option><option value="bank">Bank</option><option value="cheque">Cheque</option><option value="online">Online</option></select></div>
                <div className="ff"><label className="fl">Vendor / Paid To</label><input type="text" className="fi" value={form.vendor} onChange={sf('vendor')} placeholder="e.g. LESCO, Shell, Ali Bros" /></div>
                <div className="ff"><label className="fl">Receipt No.</label><input type="text" className="fi" value={form.receiptNo} onChange={sf('receiptNo')} placeholder="Optional" /></div>
                {['electricity','generator','water'].includes(form.category) && <>
                  <div className="ff"><label className="fl">Previous Reading</label><input type="number" className="fi" value={form.previousReading} onChange={sf('previousReading')} placeholder="Units" /></div>
                  <div className="ff"><label className="fl">Current Reading</label><input type="number" className="fi" value={form.meterReading} onChange={sf('meterReading')} placeholder="Units" /></div>
                  {form.meterReading && form.previousReading && Number(form.meterReading)>Number(form.previousReading) && (
                    <div className="ff full"><div className="req-notice" style={{marginBottom:0}}>⚡ Units consumed: {Number(form.meterReading)-Number(form.previousReading)}</div></div>
                  )}
                </>}
                <div className="ff full"><label className="fl">Note to Admin</label><textarea className="ft" value={form.note} onChange={sf('note')} placeholder="Why this expense needs to be added/edited" rows={2} /></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={submitRequest} disabled={saving}><ArrowUpTrayIcon />{saving ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </div>
        </div>

      , document.body)}

      {delTarget && createPortal(
        <div className="mb" onClick={() => setDelTarget(null)}>
          <div className="mo" style={{maxWidth:400}} onClick={e => e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">Request Delete</div><button className="mo-x" onClick={() => setDelTarget(null)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <p style={{fontSize:'.84rem',color:'#4a5075'}}>Request to delete <strong>{delTarget.title}</strong> ({fmt(delTarget.amount)})? Admin must approve this action.</p>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setDelTarget(null)}>Cancel</button>
              <button className="btn btn-red" onClick={submitDelete} disabled={saving}>{saving ? 'Submitting…' : 'Submit Delete Request'}</button>
            </div>
          </div>
        </div>

      , document.body)}
    </div>
  );
};

// ── ASSETS (same as admin — only difference is request-approval) ─────────────
const AST_SUBS = {
  furniture:    ['Chair','Table','Desk','Cupboard','Shelf','Whiteboard','Notice Board','Staff Chair','Other'],
  electronics:  ['Computer/Laptop','Projector','Printer','Scanner','TV/Monitor','Speaker','CCTV Camera','UPS','Other'],
  vehicle:      ['School Van','Bus','Motorcycle','Car','Other'],
  sports:       ['Cricket Kit','Football','Basketball','Badminton','Table Tennis','Trophy Cabinet','Other'],
  lab:          ['Microscope','Test Tubes','Bunsen Burner','Science Kit','Chemistry Set','Other'],
  generator:    ['Main Generator','Backup Generator','Inverter','Battery','Other'],
  appliance:    ['AC Unit','Fan','Water Cooler','Water Dispenser','Refrigerator','Microwave','Other'],
  miscellaneous:['Musical Instrument','Intercom','Fire Extinguisher','Other'],
};
const AST_CATEGORIES = Object.keys(AST_SUBS);
const AST_CONDITIONS = ['excellent','good','fair','poor','damaged'];
const AST_STATUSES   = ['active','inactive','disposed','under_repair'];
const AST_CAT_COLORS = {
  furniture:'#8b5cf6', electronics:'#3b6ef8', vehicle:'#10c98e', sports:'#f59e0b',
  lab:'#6366f1', generator:'#ef4444', appliance:'#14b8a6', miscellaneous:'#9094a8',
};
const AST_CAT_ICONS = {
  furniture:'🪑', electronics:'💻', vehicle:'🚌', sports:'⚽',
  lab:'🔬', generator:'⚡', appliance:'❄️', miscellaneous:'📦',
};
const AST_COND_COLOR = { excellent:'#10c98e', good:'#3b6ef8', fair:'#f59e0b', poor:'#f97316', damaged:'#ef4444' };
const AST_COND_BG    = { excellent:'#ecfdf5', good:'#eef2ff', fair:'#fffbeb', poor:'#fff7ed', damaged:'#fef2f2' };
const AST_STAT_COLOR = { active:'#10c98e', inactive:'#9094a8', disposed:'#ef4444', under_repair:'#f59e0b' };
const AST_STAT_BG    = { active:'#ecfdf5', inactive:'#f0f2f7', disposed:'#fef2f2', under_repair:'#fffbeb' };
const AST_EMPTY_FORM = { name:'', category:'furniture', subcategory:'', assetCode:'', quantity:1, location:'', condition:'good', purchaseDate:'', purchasePrice:'', vendor:'', warrantyExpiry:'', description:'', status:'active', note:'', module:'school' };
const AST_EMPTY_MAINT = { date:new Date().toISOString().slice(0,10), description:'', cost:'', performedBy:'', newCondition:'', note:'' };

const assetGridCss = `
  .cat-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .cat-tab  { padding:5px 12px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; transition:all .15s; color:#6b7194; white-space:nowrap; }
  .cat-tab:hover  { border-color:#0d9488; color:#0d9488; }
  .cat-tab.active { background:#0d9488; color:white; border-color:#0d9488; }
  .ac-emoji { width:38px; height:38px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
  .ac-badge { display:inline-flex; padding:2px 8px; border-radius:99px; font-size:.65rem; font-weight:700; white-space:nowrap; }
  .asset-list { display:flex; flex-direction:column; gap:.7rem; }
  .asset-list-item { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem; }
  .ali-top  { display:flex; align-items:center; gap:10px; margin-bottom:.7rem; }
  .ali-info { flex:1; min-width:0; }
  .ali-name { font-size:.86rem; font-weight:700; color:#0f1123; }
  .ali-meta { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .ali-row  { display:flex; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f5f6fb; }
  .ali-row:last-of-type { border-bottom:none; }
  .ali-actions { display:flex; gap:6px; margin-top:.8rem; padding-top:.7rem; border-top:1px solid #f0f2f8; }
  .maint-log { background:#fafbff; border-radius:10px; border:1px solid #e8ebf4; padding:10px 12px; margin-bottom:8px; }
  .maint-log-date { font-size:.7rem; font-weight:700; color:#0d9488; font-family:'JetBrains Mono',monospace; }
  .maint-log-desc { font-size:.78rem; color:#1a1d2e; margin-top:3px; }
  .maint-log-meta { font-size:.68rem; color:#9094a8; margin-top:3px; }
  .section-divider { font-size:.72rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.07em; margin:12px 0 8px; padding-bottom:6px; border-bottom:1px solid #f0f2f8; }
  .asset-list { display:none; }
  @media (max-width:768px) { .asset-list { display:flex; } }
`;

export const FinanceAssets = () => {
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const showModuleTabs = libraryEnabled || hostelEnabled;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [maintModal, setMaintModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [form, setForm] = useState(AST_EMPTY_FORM);
  const [maintForm, setMaintForm] = useState(AST_EMPTY_MAINT);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterModule, setFilterModule] = useState('');

  useEffect(() => { fetchAssets(); }, [filterCat, filterStatus, filterModule]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCat)    params.category = filterCat;
      if (filterStatus) params.status   = filterStatus;
      if (filterModule) params.module   = filterModule;
      const { data } = await api.get('/finance/assets', { params });
      setAssets(data || []);
    } catch { toast.error('Failed to load assets'); }
    finally { setLoading(false); }
  };

  const sf = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const handleCatChange = (cat) => setForm(f => ({ ...f, category:cat, subcategory:'' }));

  const openAdd = () => { setEditTarget(null); setForm(AST_EMPTY_FORM); setModal(true); };
  const openEdit = (a) => {
    setEditTarget(a);
    setForm({
      name:a.name, category:a.category, subcategory:a.subcategory||'',
      assetCode:a.assetCode||'', quantity:a.quantity||1, location:a.location||'',
      condition:a.condition||'good', purchaseDate:a.purchaseDate?.slice(0,10)||'',
      purchasePrice:a.purchasePrice||'', vendor:a.vendor||'',
      warrantyExpiry:a.warrantyExpiry?.slice(0,10)||'',
      description:a.description||'', status:a.status||'active', note:'',
      module:a.module||'school',
    });
    setModal(true);
  };
  const openMaint = (a) => { setSelectedAsset(a); setMaintForm(AST_EMPTY_MAINT); setMaintModal(true); };

  const submitRequest = async () => {
    if (!form.name) return toast.error('Name required');
    try {
      setSaving(true);
      const { note, ...payload } = form;
      const type = editTarget ? 'asset_edit' : 'asset_add';
      if (editTarget) payload._id = editTarget._id;
      await api.post('/finance/requests', { type, payload, note });
      toast.success('Request submitted for admin approval');
      setModal(false);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const submitDelete = async () => {
    try {
      setSaving(true);
      await api.post('/finance/requests', { type: 'asset_delete', payload: { _id: delTarget._id, name: delTarget.name }, note: `Delete asset: ${delTarget.name}` });
      toast.success('Delete request submitted');
      setDelTarget(null);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const submitMaint = async () => {
    if (!maintForm.description) return toast.error('Description is required');
    try {
      setSaving(true);
      const { note, ...rest } = maintForm;
      await api.post('/finance/requests', { type: 'asset_maintenance', payload: { ...rest, assetId: selectedAsset._id, assetName: selectedAsset.name }, note });
      toast.success('Maintenance log request submitted');
      setMaintModal(false);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const total       = assets.length;
  const active      = assets.filter(a => a.status === 'active').length;
  const underRepair = assets.filter(a => a.status === 'under_repair').length;
  const totalValue  = assets.reduce((s, a) => s + (a.purchasePrice || 0), 0);
  const subs        = AST_SUBS[form.category] || [];

  return (
    <div className="fp"><style>{sharedCss}</style><style>{assetGridCss}</style>
      <div className="fp-hdr">
        <div><div className="fp-title">Assets</div><div className="fp-sub">Track school property · request add/edit/delete/maintenance (admin approval required)</div></div>
        <button className="btn btn-teal" onClick={openAdd}><PlusIcon />Request Add</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="sc-icon" style={{background:'#eef2ff'}}>📦</div><div className="sc-label">Total Assets</div><div className="sc-value">{total}</div><div className="sc-sub">tracked items</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#ecfdf5'}}>✅</div><div className="sc-label">Active</div><div className="sc-value" style={{color:'#10c98e'}}>{active}</div><div className="sc-sub">in use</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#fffbeb'}}>🔧</div><div className="sc-label">Under Repair</div><div className="sc-value">{underRepair}</div><div className="sc-sub">being serviced</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#f3f0ff'}}>💰</div><div className="sc-label">Total Value</div><div className="sc-value" style={{fontSize:'1rem'}}>{fmt(totalValue)}</div><div className="sc-sub">purchase price</div></div>
      </div>

      {showModuleTabs && (
        <div className="cat-tabs">
          <button className={`cat-tab${!filterModule?' active':''}`} onClick={()=>setFilterModule('')}>All</button>
          {hostelEnabled  && <button className={`cat-tab${filterModule==='hostel'?' active':''}`}  onClick={()=>setFilterModule('hostel')}>🏠 Hostel</button>}
          {libraryEnabled && <button className={`cat-tab${filterModule==='library'?' active':''}`} onClick={()=>setFilterModule('library')}>📚 Library</button>}
          <button className={`cat-tab${filterModule==='school'?' active':''}`} onClick={()=>setFilterModule('school')}>🏫 School</button>
        </div>
      )}

      <div className="cat-tabs">
        <button className={`cat-tab${!filterCat?' active':''}`} onClick={() => setFilterCat('')}>All</button>
        {sortAsc(AST_CATEGORIES).map(c => (
          <button key={c} className={`cat-tab${filterCat===c?' active':''}`} onClick={() => setFilterCat(c)}>{AST_CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</button>
        ))}
      </div>

      <div className="filter-row">
        <FunnelIcon style={{width:15,height:15,stroke:'#9094a8',flexShrink:0}}/>
        <select className="fsel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {sortAsc(AST_STATUSES, s=>s.replace('_',' ')).map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={() => { setFilterCat(''); setFilterStatus(''); }}>Reset</button>
      </div>

      {loading ? <div className="loading"><div className="spin"/>Loading…</div>
      : !assets.length ? <div className="empty">No assets found. <button className="btn btn-teal btn-sm" style={{marginLeft:8}} onClick={openAdd}><PlusIcon />Add First</button></div>
      : <>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th><th>Category</th><th>Sub-type</th><th>Qty</th><th>Location</th>
                <th>Condition</th><th>Status</th><th>Value</th><th>Purchased</th><th>Warranty</th><th>Vendor</th><th>Logs</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map(a => (
                <tr key={a._id}>
                  <td style={{fontWeight:600}}>
                    {a.name}
                    {a.assetCode && <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.66rem',color:'#9094a8',marginTop:2}}>#{a.assetCode}</div>}
                  </td>
                  <td><span className="ac-badge" style={{background:AST_CAT_COLORS[a.category]+'22',color:AST_CAT_COLORS[a.category]}}>{AST_CAT_ICONS[a.category]} {a.category}</span></td>
                  <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.subcategory||'—'}</td>
                  <td>{a.quantity||1}</td>
                  <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.location||'—'}</td>
                  <td><span className="ac-badge" style={{background:AST_COND_BG[a.condition],color:AST_COND_COLOR[a.condition]}}>{a.condition}</span></td>
                  <td><span className="ac-badge" style={{background:AST_STAT_BG[a.status],color:AST_STAT_COLOR[a.status]}}>{a.status.replace('_',' ')}</span></td>
                  <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{fmt(a.purchasePrice)}</td>
                  <td style={{fontSize:'.76rem',color:'#6b7194'}}>{fmtDate(a.purchaseDate)}</td>
                  <td style={{fontSize:'.76rem',color:a.warrantyExpiry&&new Date(a.warrantyExpiry)<new Date()?'#ef4444':'#6b7194'}}>{fmtDate(a.warrantyExpiry)}</td>
                  <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.vendor||'—'}</td>
                  <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.maintenanceLogs?.length||0}</td>
                  <td>
                    <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openMaint(a)}>🔧</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(a)}><PencilSquareIcon /></button>
                      <button className="btn btn-red btn-sm" onClick={() => setDelTarget(a)}><TrashIcon /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="asset-list">
          {assets.map(a => (
            <div key={a._id} className="asset-list-item">
              <div className="ali-top">
                <div className="ac-emoji" style={{background:AST_CAT_COLORS[a.category]+'22'}}>{AST_CAT_ICONS[a.category]}</div>
                <div className="ali-info">
                  <div className="ali-name">{a.name}</div>
                  <div className="ali-meta">{a.subcategory||a.category} · {a.location || 'No location'}</div>
                </div>
                <span className="ac-badge" style={{background:AST_STAT_BG[a.status],color:AST_STAT_COLOR[a.status],flexShrink:0}}>{a.status.replace('_',' ')}</span>
              </div>
              <div className="ali-row"><span style={{color:'#9094a8'}}>Condition</span><span className="ac-badge" style={{background:AST_COND_BG[a.condition],color:AST_COND_COLOR[a.condition]}}>{a.condition}</span></div>
              {a.purchasePrice>0 && <div className="ali-row"><span style={{color:'#9094a8'}}>Value</span><span style={{fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(a.purchasePrice)}</span></div>}
              {a.purchaseDate && <div className="ali-row"><span style={{color:'#9094a8'}}>Purchased</span><span>{fmtDate(a.purchaseDate)}</span></div>}
              <div className="ali-actions">
                <button className="btn btn-ghost btn-sm" style={{flex:1,justifyContent:'center'}} onClick={() => openMaint(a)}>🔧 Maintain</button>
                <button className="btn btn-ghost btn-sm" style={{flex:1,justifyContent:'center'}} onClick={() => openEdit(a)}><PencilSquareIcon />Edit</button>
                <button className="btn btn-red btn-sm" style={{flex:1,justifyContent:'center'}} onClick={() => setDelTarget(a)}><TrashIcon />Delete</button>
              </div>
            </div>
          ))}
        </div>
      </>}

      {modal && createPortal(
        <div className="mb" onClick={() => setModal(false)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">{editTarget ? 'Request Edit Asset' : 'Request Add Asset'}</div><button className="mo-x" onClick={() => setModal(false)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <div className="req-notice">This will be sent to admin for approval before taking effect.</div>
              <div className="fg">
                {showModuleTabs && (
                  <div className="ff"><label className="fl">Belongs To</label><select className="fs" value={form.module} onChange={sf('module')}>
                    <option value="school">🏫 School</option>
                    {hostelEnabled  && <option value="hostel">🏠 Hostel</option>}
                    {libraryEnabled && <option value="library">📚 Library</option>}
                  </select></div>
                )}
                <div className="ff"><label className="fl">Category</label><select className="fs" value={form.category} onChange={e => handleCatChange(e.target.value)}>{AST_CATEGORIES.map(c => <option key={c} value={c}>{AST_CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}</select></div>
                <div className="ff"><label className="fl">Type / Sub-category</label><select className="fs" value={form.subcategory} onChange={sf('subcategory')}><option value="">— Select type —</option>{subs.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="ff full"><label className="fl">Name *</label><input className="fi" value={form.name} onChange={sf('name')} placeholder={form.subcategory?`e.g. ${form.subcategory} - Room 5`:'e.g. Wooden Chair - Room 5'} /></div>
                <div className="ff"><label className="fl">Asset Code / Tag</label><input className="fi" value={form.assetCode} onChange={sf('assetCode')} placeholder="e.g. CHR-001" /></div>
                <div className="ff"><label className="fl">Quantity</label><input type="number" className="fi" min={1} value={form.quantity} onChange={sf('quantity')} /></div>
                <div className="ff"><label className="fl">Location</label><input className="fi" value={form.location} onChange={sf('location')} placeholder="e.g. Room 5, Block A" /></div>
                <div className="ff"><label className="fl">Condition</label><select className="fs" value={form.condition} onChange={sf('condition')}>{AST_CONDITIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}</select></div>
                <div className="ff"><label className="fl">Status</label><select className="fs" value={form.status} onChange={sf('status')}>{AST_STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}</select></div>
                <div className="ff"><label className="fl">Purchase Price (Rs.)</label><input type="number" className="fi" value={form.purchasePrice} onChange={sf('purchasePrice')} placeholder="0" /></div>
                <div className="ff"><label className="fl">Purchase Date</label><input type="date" className="fi" value={form.purchaseDate} onChange={sf('purchaseDate')} /></div>
                <div className="ff"><label className="fl">Vendor / Supplier</label><input className="fi" value={form.vendor} onChange={sf('vendor')} placeholder="e.g. Ali Furniture" /></div>
                <div className="ff"><label className="fl">Warranty Expiry</label><input type="date" className="fi" value={form.warrantyExpiry} onChange={sf('warrantyExpiry')} /></div>
                <div className="ff full"><label className="fl">Description / Notes</label><textarea className="ft" value={form.description} onChange={sf('description')} placeholder="Optional notes..." /></div>
                <div className="ff full"><label className="fl">Note to Admin</label><textarea className="ft" value={form.note} onChange={sf('note')} placeholder="Why this asset needs to be added/edited" rows={2} /></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={submitRequest} disabled={saving}><ArrowUpTrayIcon />{saving ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </div>
        </div>

      , document.body)}

      {delTarget && createPortal(
        <div className="mb" onClick={() => setDelTarget(null)}>
          <div className="mo" style={{maxWidth:400}} onClick={e => e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">Request Delete</div><button className="mo-x" onClick={() => setDelTarget(null)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <p style={{fontSize:'.84rem',color:'#4a5075'}}>Request to delete <strong>{delTarget.name}</strong>? Admin must approve this action.</p>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setDelTarget(null)}>Cancel</button>
              <button className="btn btn-red" onClick={submitDelete} disabled={saving}>{saving ? 'Submitting…' : 'Submit Delete Request'}</button>
            </div>
          </div>
        </div>

      , document.body)}

      {maintModal && selectedAsset && createPortal(
        <div className="mb" onClick={() => setMaintModal(false)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">🔧 Maintenance — {selectedAsset.name}</div><button className="mo-x" onClick={() => setMaintModal(false)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <div className="req-notice">This log will be sent to admin for approval before being added.</div>
              {selectedAsset.maintenanceLogs?.length>0 && (
                <>
                  <div className="section-divider">Previous Logs ({selectedAsset.maintenanceLogs.length})</div>
                  {[...selectedAsset.maintenanceLogs].reverse().slice(0,3).map((log,i) => (
                    <div key={i} className="maint-log">
                      <div className="maint-log-date">{fmtDate(log.date)}</div>
                      <div className="maint-log-desc">{log.description}</div>
                      <div className="maint-log-meta">{log.cost>0 && `Cost: Rs. ${Number(log.cost).toLocaleString()}`}{log.performedBy && ` · By: ${log.performedBy}`}</div>
                    </div>
                  ))}
                </>
              )}
              <div className="section-divider">Add New Log</div>
              <div className="fg">
                <div className="ff"><label className="fl">Date *</label><input type="date" className="fi" value={maintForm.date} onChange={e => setMaintForm(f => ({ ...f, date:e.target.value }))} /></div>
                <div className="ff"><label className="fl">Cost (Rs.)</label><input type="number" className="fi" value={maintForm.cost} onChange={e => setMaintForm(f => ({ ...f, cost:e.target.value }))} placeholder="0" /></div>
                <div className="ff full"><label className="fl">Description *</label><textarea className="ft" value={maintForm.description} onChange={e => setMaintForm(f => ({ ...f, description:e.target.value }))} placeholder="What was done? e.g. Engine oil changed, filter replaced" /></div>
                <div className="ff"><label className="fl">Performed By</label><input className="fi" value={maintForm.performedBy} onChange={e => setMaintForm(f => ({ ...f, performedBy:e.target.value }))} placeholder="Technician / vendor name" /></div>
                <div className="ff"><label className="fl">Update Condition</label><select className="fs" value={maintForm.newCondition} onChange={e => setMaintForm(f => ({ ...f, newCondition:e.target.value }))}><option value="">— No change —</option>{AST_CONDITIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}</select></div>
                <div className="ff full"><label className="fl">Note to Admin</label><textarea className="ft" value={maintForm.note} onChange={e => setMaintForm(f => ({ ...f, note:e.target.value }))} rows={2} /></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setMaintModal(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={submitMaint} disabled={saving}><ArrowUpTrayIcon />{saving ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </div>
        </div>

      , document.body)}
    </div>
  );
};

// ── SALARY (view only) ────────────────────────────────────────────────────────
export const FinanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : '';
    api.get(`/finance/requests${q}`).then(r => setRequests(r.data || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [filter]);

  const TYPE_LABEL = {
    fee_action:'Fee Action', expense_add:'Add Expense', expense_edit:'Edit Expense',
    expense_delete:'Delete Expense', asset_add:'Add Asset', asset_edit:'Edit Asset',
    asset_delete:'Delete Asset', asset_maintenance:'Asset Maintenance',
    salary_action:'Salary Action', letter_character:'Character Certificate',
    letter_school_leaving:'School Leaving Cert', letter_experience:'Experience Letter',
    staff_attendance_mark:'Staff Attendance',
    fee_mark_paid:'Fee — Mark Paid', fee_add_fine:'Fee — Add Fine',
    fee_discount:'Fee — Discount', fee_edit:'Fee — Edit Record',
    fee_bulk_generate:'Fee — Bulk Generate',
    staff_leave_add:'Leave — Add', staff_leave_review:'Leave — Review',
    librarian_add:'Librarian — Add', librarian_exit:'Librarian — Exit', librarian_reactivate:'Librarian — Reactivate',
    hostel_member_add:'Hostel Member — Add', hostel_member_exit:'Hostel Member — Exit', hostel_member_reactivate:'Hostel Member — Reactivate',
  };

  return (
    <div className="fp"><style>{sharedCss}</style>
      <div className="fp-hdr">
        <div><div className="fp-title">My Requests</div><div className="fp-sub">Track all submitted requests and their status</div></div>
      </div>
      <div className="tabs">
        {[{key:'',label:'All'},{key:'approved',label:'Approved'},{key:'pending',label:'Pending'},{key:'rejected',label:'Rejected'}].map(s => (
          <button key={s.key} className={`tab${filter===s.key?' on':''}`} onClick={() => setFilter(s.key)}>{s.label}</button>
        ))}
      </div>
      <div className="panel">
        <div className="panel-hdr"><div className="panel-title">Requests ({requests.length})</div></div>
        {loading ? <div className="loading"><div className="spin"/>Loading…</div>
        : !requests.length ? <div className="empty">No requests found.</div>
        : <>
          <div className="tbl-wrap" style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Type</th><th>Status</th><th>Your Note</th><th>Admin Note</th><th>Date</th></tr></thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r._id}>
                    <td style={{fontWeight:600}}>{TYPE_LABEL[r.type] || r.type}</td>
                    <td><span className={`pill pill-${r.status}`}>{r.status}</span></td>
                    <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:180}}>{r.note || '—'}</td>
                    <td style={{fontSize:'.76rem',color:r.status==='rejected'?'#ef4444':'#10c98e',maxWidth:180}}>{r.adminNote || '—'}</td>
                    <td><span className="mono">{fmtDateDMY(r.createdAt)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fp-cards">
            {requests.map(r => (
              <div key={r._id} className="fp-card">
                <div className="fp-card-top">
                  <div className="fp-card-title">{TYPE_LABEL[r.type] || r.type}</div>
                  <span className={`pill pill-${r.status}`}>{r.status}</span>
                </div>
                <div className="fp-card-row"><span className="fp-card-label">Your Note</span><span className="fp-card-val">{r.note || '—'}</span></div>
                <div className="fp-card-row"><span className="fp-card-label">Admin Note</span><span className="fp-card-val" style={{color:r.status==='rejected'?'#ef4444':'#10c98e'}}>{r.adminNote || '—'}</span></div>
                <div className="fp-card-row"><span className="fp-card-label">Date</span><span className="mono">{fmtDateDMY(r.createdAt)}</span></div>
              </div>
            ))}
          </div>
          </>}
      </div>
    </div>
  );
};

// ── NOTICES ────────────────────────────────────────────────────────────────────
const FINANCE_NOTICE_ROLES = [
  { value:'teacher',     label:'Teacher'     },
  { value:'coordinator', label:'Coordinator' },
  { value:'student',     label:'Student'     },
];

export const FinanceNotices = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title:'', content:'' });

  const [mode, setMode] = useState('broadcast'); // 'broadcast' | 'specific'
  const [role, setRole] = useState('teacher');
  const [people, setPeople] = useState([]);
  const [targetUserId, setTargetUserId] = useState('');
  const [classes, setClasses] = useState([]);
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');

  const fetchNotices = () => {
    setLoading(true);
    api.get('/finance/notices').then(r => setNotices(r.data || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchNotices();
    api.post('/finance/notices/mark-read').catch(() => {});
  }, []);

  useEffect(() => { api.get('/finance/classes').then(r => setClasses(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!showModal) return;
    setTargetUserId('');
    if (role === 'teacher') {
      api.get('/finance/letter-teachers').then(r => setPeople(r.data || [])).catch(() => setPeople([]));
    } else if (role === 'coordinator') {
      api.get('/finance/coordinators').then(r => setPeople(r.data || [])).catch(() => setPeople([]));
    } else {
      setPeople([]); // students loaded by the class/section effect below
    }
  }, [showModal, role]);

  useEffect(() => {
    if (!showModal || role !== 'student') return;
    setTargetUserId('');
    if (!filterClass) { setPeople([]); return; }
    const params = new URLSearchParams({ classId: filterClass });
    if (filterSection) params.set('section', filterSection);
    api.get(`/finance/letter-students?${params}`).then(r => setPeople(r.data || [])).catch(() => setPeople([]));
  }, [showModal, role, filterClass, filterSection]);

  const personValue = (p) => p.user?._id || p._id;
  const personLabel = (p) => `${p.user?.name || p.name} — ${p.user?.email || p.email || ''}`;

  const openModal = () => {
    setForm({ title:'', content:'' }); setMode('broadcast'); setRole('teacher');
    setTargetUserId(''); setFilterClass(''); setFilterSection('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    if (mode === 'specific' && !targetUserId) { toast.error('Select a person'); return; }
    try {
      setSaving(true);
      const payload = { title: form.title, content: form.content, audience: [role],
        targetUsers: mode === 'specific' ? [targetUserId] : [] };
      await api.post('/finance/notices', payload);
      toast.success('Notice posted');
      setShowModal(false);
      fetchNotices();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const roleColor = (r) => r === 'admin' ? '#3b6ef8' : r === 'coordinator' ? '#f97316' : '#6b7194';

  return (
    <div className="fp"><style>{sharedCss}</style>
      <div className="fp-hdr">
        <div><div className="fp-title">Notices</div><div className="fp-sub">Announcements from admin, and post your own</div></div>
        <button className="btn btn-teal" onClick={openModal}><PlusIcon />Post Notice</button>
      </div>
      <div className="panel">
        <div className="panel-hdr"><div className="panel-title">All Notices ({notices.length})</div></div>
        {loading ? <div className="loading"><div className="spin"/>Loading…</div>
        : !notices.length ? <div className="empty">No notices yet.</div>
        : <div style={{padding:'10px 18px'}}>
            {notices.map(n => (
              <div key={n._id} style={{padding:'12px 0',borderBottom:'1px solid #f0f2f8'}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:6,flexWrap:'wrap'}}>
                  <div style={{fontWeight:700,fontSize:'.86rem',color:'#0f1123'}}>{n.title}</div>
                  <span style={{fontSize:'.67rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:`${roleColor(n.postedBy?.role)}18`,color:roleColor(n.postedBy?.role),whiteSpace:'nowrap',flexShrink:0}}>{n.postedBy?.role}</span>
                </div>
                <div style={{fontSize:'.8rem',color:'#4a5075',lineHeight:1.6,marginBottom:8}}>{n.content}</div>
                <div style={{fontSize:'.7rem',color:'#9094a8'}}>{n.postedBy?.name} · {fmtDateDMY(n.createdAt)}</div>
              </div>
            ))}
          </div>}
      </div>

      {showModal && createPortal(
        <div className="mb" onClick={() => setShowModal(false)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">Post Notice</div><button className="mo-x" onClick={() => setShowModal(false)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <div className="fg">
                <div className="ff full"><label className="fl">Title</label><input className="fi" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Notice title…" /></div>
                <div className="ff full"><label className="fl">Content</label><textarea className="ft" rows={3} value={form.content} onChange={e=>setForm({...form,content:e.target.value})} placeholder="Write the notice…" /></div>
                <div className="ff full"><label className="fl">Send To</label>
                  <div style={{display:'flex',gap:6}}>
                    <button type="button" className={`btn ${mode==='broadcast'?'btn-teal':'btn-ghost'} btn-sm`} style={{flex:1,justifyContent:'center'}} onClick={()=>setMode('broadcast')}>All in role</button>
                    <button type="button" className={`btn ${mode==='specific'?'btn-teal':'btn-ghost'} btn-sm`} style={{flex:1,justifyContent:'center'}} onClick={()=>setMode('specific')}>Specific person</button>
                  </div>
                </div>
                <div className="ff"><label className="fl">Role</label>
                  <select className="fs" value={role} onChange={e=>setRole(e.target.value)}>
                    {FINANCE_NOTICE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {mode === 'specific' && role === 'student' && (
                  <>
                    <div className="ff"><label className="fl">Class</label>
                      <select className="fs" value={filterClass} onChange={e=>{setFilterClass(e.target.value);setFilterSection('');}}>
                        <option value="">Select class…</option>
                        {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="ff"><label className="fl">Section</label>
                      <select className="fs" value={filterSection} onChange={e=>setFilterSection(e.target.value)} disabled={!filterClass}>
                        <option value="">All sections</option>
                        {(classes.find(c=>String(c._id)===String(filterClass))?.sections||[]).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </>
                )}
                {mode === 'specific' && (
                  <div className="ff full"><label className="fl">Person</label>
                    <select className="fs" value={targetUserId} onChange={e=>setTargetUserId(e.target.value)} disabled={role==='student'&&!filterClass}>
                      <option value="">{people.length ? 'Select…' : (role==='student'&&!filterClass ? 'Select a class first' : 'No results')}</option>
                      {people.map(p => <option key={personValue(p)} value={personValue(p)}>{personLabel(p)}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={handleSubmit} disabled={saving}>{saving ? 'Posting…' : 'Post Notice'}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default FinanceDashboard;